import {
  CheckOutlined,
  CloudUploadOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont, tabularNums } from '@medcom/shared-ui/styles/globalCss.ts';
import { antdTokenFilter, MIN_TOKEN_LENGTH_PG_TRGM, shortTokensDropped, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Alert, Button, Flex, Input, Popover, Select, Table, type TablePaginationConfig, Tag, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import type React from 'react';
import { type Key, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { MainContent } from '../../components/MainContent';
import { TableColumnSettings } from '../../components/TableColumnSettings';
import { TableList } from '../../components/TableList.tsx';
import { getFragmentData } from '../../graphql/generated';
import {
  type DoctorFilter,
  DoctorStateFilter,
  type Pageable,
  SortOrder,
  type UpdateDoctorModal_DoctorFragment,
} from '../../graphql/generated/graphql.ts';
import type { TableColumnSetting } from '../../hooks/store/useUserSettingsStore.ts';
import { SHORT_DATE_FORMAT } from '../../utils/dateFormatUtils';
import { translateBillingPreference } from '../../utils/enumHelpers';
import { DOCTORS_META_QUERY, PAGED_DOCTORS_QUERY } from './Doctors.gql';
import { AssignedUsersPopover } from './doctors/AssignedUsersPopover.tsx';
import { CreateDoctorModal } from './doctors/CreateDoctorModal';
import { CatalogFragment, LabFragment, TagFragment } from './doctors/DoctorForm.gql';
import { ImportLookupDoctor } from './doctors/ImportLookupDoctor.tsx';
import { TagManagementModal } from './doctors/TagManagementModal.tsx';
import { compareByTag } from './doctors/tagColors.ts';
import { UpdateDoctorModal } from './doctors/UpdateDoctorModal';
import { DoctorFragment } from './doctors/UpdateDoctorModal.gql';

const { Option, OptGroup } = Select;

const doctorSearchParams = {
  search: parseAsString.withDefault(''),
  labId: parseAsString,
  catalogId: parseAsString,
  tagIds: parseAsArrayOf(parseAsString).withDefault([]),
  state: parseAsStringLiteral([DoctorStateFilter.ALL, DoctorStateFilter.ACTIVE, DoctorStateFilter.INACTIVE]).withDefault(
    DoctorStateFilter.ALL
  ),
  sortBy: parseAsString.withDefault('name'),
  sortOrder: parseAsStringLiteral([SortOrder.ASC, SortOrder.DESC]).withDefault(SortOrder.ASC),
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(10),
};

// `state` is reserved by OIDC: the Keycloak callback appends its own to the URL, and a
// second one makes oidc-client-ts read ours instead and fail the login (LC-980)
const doctorSearchUrlKeys = { state: 'status' };

export const Doctors: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editDoctor, setEditDoctor] = useState<UpdateDoctorModal_DoctorFragment | null>(null);
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const { token } = theme.useToken();

  const [urlState, setUrlState] = useQueryStates(doctorSearchParams, { urlKeys: doctorSearchUrlKeys });
  const [searchValue, setSearchValue] = useState(urlState.search);
  const debouncedSearch = useDebouncedCallback((value: string) => setUrlState({ search: value, page: 1 }), 300);

  const filter: DoctorFilter = {
    search: urlState.search,
    labId: urlState.labId,
    catalogId: urlState.catalogId,
    tagIds: urlState.tagIds.length > 0 ? urlState.tagIds : null,
    state: urlState.state,
  };

  const pageable: Pageable = {
    sortBy: urlState.sortBy,
    sortOrder: urlState.sortOrder,
    offset: (urlState.page - 1) * urlState.pageSize,
    limit: urlState.pageSize,
  };

  const droppedTokens = shortTokensDropped(filter.search);
  const validBackendTokens = tokenize(filter.search, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM });
  const searchHasOnlyShortTokens = filter.search.trim() !== '' && validBackendTokens.length === 0;

  const {
    data: pagedData,
    previousData,
    loading,
    refetch,
  } = useQuery(PAGED_DOCTORS_QUERY, {
    // Send only tokens that pass the backend's pg_trgm minimum length.
    variables: { pageable, filter: { ...filter, search: validBackendTokens.join(' ') } },
    pollInterval: 60000,
    skip: searchHasOnlyShortTokens,
  });

  const { data: metaData, refetch: refetchMeta } = useQuery(DOCTORS_META_QUERY);

  // reset page if state is invalid, e.g. when searching on last page
  if (
    pagedData?.pagedDoctors &&
    pagedData.pagedDoctors.totalCount > 0 &&
    (urlState.page - 1) * urlState.pageSize >= pagedData.pagedDoctors.totalCount
  ) {
    setUrlState({ page: 1 });
  }

  const entries = pagedData?.pagedDoctors?.entries ?? previousData?.pagedDoctors?.entries;
  const doctors = getFragmentData(DoctorFragment, entries) ?? [];

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<UpdateDoctorModal_DoctorFragment> | SorterResult<UpdateDoctorModal_DoctorFragment>[]
  ) => {
    setUrlState({
      sortBy: (sorter as SorterResult<UpdateDoctorModal_DoctorFragment>).field?.toString() || 'name',
      sortOrder: (sorter as SorterResult<UpdateDoctorModal_DoctorFragment>).order === 'descend' ? SortOrder.DESC : SortOrder.ASC,
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const allMeAddresses = metaData?.allDoctorMeAddresses ?? [];
  const allTags = useMemo(
    () => [...(metaData?.tags ?? [])].map(it => getFragmentData(TagFragment, it)).sort(compareByTag),
    [metaData?.tags]
  );

  const groupedCatalogs = useMemo(() => {
    const catalogs = (metaData?.catalogs ?? []).map(it => getFragmentData(CatalogFragment, it));
    const map = new Map<string, typeof catalogs>();
    for (const c of catalogs) {
      const key = c.lab?.shortName ?? 'Andere';
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
      map.set(k, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [metaData?.catalogs]);

  const [columnSettings, setColumnSettings] = useState<TableColumnSetting[]>([
    { id: 'name', visible: true, title: 'Anzeigename', checkable: false },
    { id: 'disabled', visible: true, title: 'Aktiv', checkable: true },
    { id: 'assignedUserCount', visible: true, title: 'Benutzer', checkable: true },
    { id: 'meAddress', visible: true, title: 'ME-Adresse', checkable: true },
    { id: 'hvNumber', visible: false, title: 'HV-Nummer', checkable: true },
    { id: 'firstName', visible: true, title: 'Vorname', checkable: true },
    { id: 'lastName', visible: true, title: 'Nachname / Firma', checkable: true },
    { id: 'email', visible: false, title: 'E-Mail', checkable: true },
    { id: 'phone', visible: true, title: 'Telefon', checkable: true },
    { id: 'phoneContacts', visible: false, title: 'Telefonkontakte', checkable: true },
    { id: 'street', visible: false, title: 'Straße', checkable: true },
    { id: 'city', visible: false, title: 'Ort', checkable: true },
    { id: 'zip', visible: false, title: 'PLZ', checkable: true },
    { id: 'assignedLabs', visible: true, title: 'Labors', checkable: true },
    { id: 'lg', visible: false, title: 'LG', checkable: true },
    { id: 'tags', visible: true, title: 'Tags', checkable: true },
    { id: 'catalogs', visible: false, title: 'Kataloge', checkable: true },
    { id: 'theme', visible: false, title: 'Theme', checkable: true },
    { id: 'specialist', visible: false, title: 'Fachrichtung', checkable: true },
    { id: 'aisImportDoctorId', visible: false, title: 'Multi-Seat X-Arzt', checkable: true },
    { id: 'billingPreference', visible: false, title: 'Verrechnung', checkable: true },
    { id: 'comment', visible: false, title: 'Anmerkung', checkable: true },
    { id: 'hasAis', visible: false, title: 'AIS', checkable: true },
    { id: 'hasAisLink', visible: false, title: 'Web-AIS', checkable: true },
    { id: 'ecardEnabled', visible: false, title: 'e-card', checkable: true },
    { id: 'aiEnabled', visible: false, title: 'KI', checkable: true },
    { id: 'installDate', visible: false, title: 'Installationsdatum', checkable: true },
  ]);

  const columns: ColumnsType<UpdateDoctorModal_DoctorFragment> = [
    {
      title: 'Anzeigename',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 130,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'Aktiv',
      dataIndex: 'disabled',
      key: 'disabled',
      width: 70,
      sorter: true,
      render: value => (!value ? <CheckOutlined /> : ''),
    },
    {
      title: 'ME-Adresse',
      dataIndex: 'meAddress',
      key: 'meAddress',
      ellipsis: true,
      width: 120,
      sorter: true,
      render: value => (
        <span className={monoFont}>
          <Markable tokens={validBackendTokens}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'HV-Nummer',
      dataIndex: 'hvNumber',
      key: 'hvNumber',
      ellipsis: true,
      width: 110,
      sorter: true,
      render: value => <span className={monoFont}>{value}</span>,
    },
    {
      title: 'Vorname',
      dataIndex: 'firstName',
      key: 'firstName',
      ellipsis: true,
      width: 100,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'Nachname / Firma',
      dataIndex: 'lastName',
      key: 'lastName',
      ellipsis: true,
      width: 160,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'Benutzer',
      dataIndex: 'assignedUserCount',
      key: 'assignedUserCount',
      width: 80,
      render: (value, record) => <AssignedUsersPopover doctorId={record.id} count={value} />,
    },
    {
      title: 'E-Mail',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      width: 160,
      sorter: true,
      render: value =>
        value ? (
          <a href={`mailto:${value}`} onClick={e => e.stopPropagation()}>
            {value}
          </a>
        ) : null,
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      ellipsis: true,
      width: 120,
      sorter: true,
      render: value =>
        value ? (
          <a href={`tel:${value}`} onClick={e => e.stopPropagation()}>
            {value}
          </a>
        ) : null,
    },
    {
      title: 'Straße',
      dataIndex: 'street',
      key: 'street',
      ellipsis: true,
      width: 160,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'Ort',
      dataIndex: 'city',
      key: 'city',
      ellipsis: true,
      width: 100,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'PLZ',
      dataIndex: 'zip',
      key: 'zip',
      ellipsis: true,
      width: 80,
      sorter: true,
      render: value => <Markable tokens={validBackendTokens}>{value}</Markable>,
    },
    {
      title: 'Labors',
      dataIndex: 'assignedLabs',
      key: 'assignedLabs',
      width: 90,
      render: (_, record) => (
        <TableList
          entries={record.assignedLabs.map(it => (
            <Tag key={it.lab.id}>
              {it.lab.name}
              {it.customerId ? ':' : null} <Markable tokens={validBackendTokens}>{it.customerId}</Markable>
            </Tag>
          ))}
          maxEntries={3}
          raw
        />
      ),
      ellipsis: true,
    },
    {
      title: 'LG',
      key: 'lg',
      width: 60,
      render: (_, record) => (record.lgDoctorBilling || record.lgInsurances.length > 0 ? <CheckOutlined /> : ''),
    },
    {
      title: 'Tags',
      key: 'tags',
      width: 150,
      render: (_, record) => (
        <TableList
          entries={[...record.tags].sort(compareByTag).map(tag => (
            <Tag key={tag.id} color={tag.color}>
              {tag.name}
            </Tag>
          ))}
          maxEntries={5}
          raw
        />
      ),
    },
    {
      title: 'Kataloge',
      dataIndex: 'catalogs',
      key: 'catalogs',
      width: 100,
      render: (_, record) => <TableList entries={record.catalogs.map(it => `${it.lab.shortName} - ${it.name}`)} maxEntries={3} />,
      ellipsis: true,
    },
    {
      title: 'Theme',
      dataIndex: 'theme',
      key: 'theme',
      width: 90,
      render: (_, record) => record?.theme?.name,
      ellipsis: true,
    },
    {
      title: 'Fachrichtung',
      dataIndex: 'specialist',
      key: 'specialist',
      ellipsis: true,
      width: 140,
      sorter: true,
    },
    {
      title: 'Multi-Seat X-Arzt',
      dataIndex: 'aisImportDoctorId',
      key: 'aisImportDoctorId',
      width: 160,
      ellipsis: true,
      sorter: true,
      render: value => (
        <span className={monoFont}>
          <Markable tokens={validBackendTokens}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Verrechnung',
      dataIndex: 'billingPreference',
      key: 'billingPreference',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: value => translateBillingPreference(value),
    },
    {
      title: 'Anmerkung',
      dataIndex: 'comment',
      key: 'comment',
      width: 100,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <span onClick={e => e.stopPropagation()}>
            <Popover trigger={['click']} content={<div style={{ whiteSpace: 'pre-wrap', maxWidth: '500px' }}>{value}</div>}>
              <Button
                type="link"
                className={css`
                  height: 20px;
                  margin: 0;
                  padding: 0;
                `}
                icon={<EyeOutlined />}
              />
            </Popover>
          </span>
        );
      },
    },
    {
      title: 'AIS',
      dataIndex: 'hasAis',
      key: 'hasAis',
      width: 70,
      sorter: true,
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Web-AIS',
      dataIndex: 'hasAisLink',
      key: 'hasAisLink',
      width: 100,
      sorter: true,
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'e-card',
      dataIndex: 'ecardEnabled',
      key: 'ecardEnabled',
      width: 80,
      sorter: true,
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'KI',
      dataIndex: 'aiEnabled',
      key: 'aiEnabled',
      width: 60,
      sorter: true,
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Installationsdatum',
      dataIndex: 'installDate',
      key: 'installDate',
      width: 130,
      sorter: true,
      render: value => (value ? <span className={tabularNums}>{dayjs(value).format(SHORT_DATE_FORMAT)}</span> : ''),
    },
    {
      title: 'Telefonkontakte',
      key: 'phoneContacts',
      width: 200,
      render: (_, record) => (
        <TableList
          entries={record.phoneContacts.map(pc => (
            <span key={pc.id} onClick={e => e.stopPropagation()}>
              {pc.name}: <a href={`tel:${pc.phoneNumber}`}>{pc.phoneNumber}</a>
            </span>
          ))}
          maxEntries={2}
          raw
        />
      ),
      ellipsis: true,
    },
  ];

  const filteredColumns: ColumnsType<UpdateDoctorModal_DoctorFragment> = columnSettings
    .filter(cs => cs.visible)
    .map(cs => columns.find(c => c.key === cs.id)!)
    .map(col =>
      col.sorter
        ? {
            ...col,
            sortOrder:
              col.key === urlState.sortBy
                ? urlState.sortOrder === SortOrder.DESC
                  ? ('descend' as const)
                  : ('ascend' as const)
                : undefined,
          }
        : col
    );

  return (
    <MainContent size="full">
      <PageHeader
        title="Zuweiser"
        extra={
          <Flex gap={token.paddingXS} wrap align="center" justify="flex-end">
            <Select
              style={{ width: '130px' }}
              onChange={(value: DoctorStateFilter) => setUrlState({ state: value, page: 1 })}
              value={urlState.state}
              options={[
                { label: 'Alle', value: DoctorStateFilter.ALL },
                { label: 'Nur Aktive', value: DoctorStateFilter.ACTIVE },
                { label: 'Nur Inaktive', value: DoctorStateFilter.INACTIVE },
              ]}
            />
            <Input
              autoFocus
              allowClear
              placeholder="Suche"
              value={searchValue}
              onChange={e => {
                setSearchValue(e.target.value);
                debouncedSearch(e.target.value);
              }}
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title="Suche nach Anzeigename, ME-Adresse, Kundennummer, Ort, Vorname, Nachname oder Multi-Seat-X-Arzt">
                  <InfoCircleOutlined />
                </Tooltip>
              }
              style={{ width: '250px' }}
            />
            <Select
              allowClear
              showSearch={{ filterOption: antdTokenFilter }}
              placeholder="Labor-Filter"
              popupMatchSelectWidth={false}
              style={{ width: '200px' }}
              onChange={(value: string | undefined) => setUrlState({ labId: value ?? null, page: 1 })}
              value={urlState.labId || undefined}
            >
              {metaData?.labs?.map(l => {
                const lab = getFragmentData(LabFragment, l);
                return (
                  <Option key={lab.id} value={lab.id} search={lab.name}>
                    {lab.name}
                  </Option>
                );
              })}
            </Select>
            <Select
              allowClear
              showSearch={{ filterOption: antdTokenFilter }}
              placeholder="Katalog-Filter"
              popupMatchSelectWidth={false}
              style={{ width: '200px' }}
              onChange={(value: string | undefined) => setUrlState({ catalogId: value ?? null, page: 1 })}
              value={urlState.catalogId || undefined}
            >
              {groupedCatalogs.map(([shortName, group]) => (
                <OptGroup key={shortName} label={shortName}>
                  {group.map(catalog => (
                    <Option key={catalog.id} value={catalog.id} search={`${catalog.lab.shortName} ${catalog.name}`}>
                      {catalog.name}
                    </Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
            <Select
              allowClear
              mode="multiple"
              showSearch={{ filterOption: antdTokenFilter }}
              placeholder="Tag-Filter"
              popupMatchSelectWidth={false}
              style={{ minWidth: '200px' }}
              onChange={(values: string[]) => setUrlState({ tagIds: values.length > 0 ? values : [], page: 1 })}
              value={urlState.tagIds}
              tagRender={props => {
                const tag = allTags.find(t => t.id === props.value);
                return (
                  <Tag color={tag?.color} closable onClose={props.onClose} style={{ marginInlineEnd: 4 }}>
                    {tag?.name}
                  </Tag>
                );
              }}
            >
              {allTags.map(tag => (
                <Option key={tag.id} value={tag.id} search={tag.name}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              ))}
            </Select>
            <Button icon={<TagsOutlined />} onClick={() => setTagManagementOpen(true)}>
              Tags
            </Button>
            <Popover
              title="Zuweiserliste für PV Lookup importieren"
              content={<ImportLookupDoctor />}
              placement="bottomRight"
              arrow
              trigger={['click']}
            >
              <Button icon={<CloudUploadOutlined />}>Import</Button>
            </Popover>
            <Button icon={<PlusCircleOutlined />} type="primary" onClick={() => setCreateModalOpen(true)}>
              Neuer Zuweiser
            </Button>
            <TableColumnSettings tableKey="doctors" initialColumnSettings={columnSettings} onChange={setColumnSettings} />
          </Flex>
        }
        className={css`
          padding: 0;
          padding-bottom: inherit;
          .ant-page-header-heading-left {
            flex-shrink: 0;
          }
        `}
      />
      {droppedTokens.length > 0 && (
        <Alert
          showIcon
          type="warning"
          title={`Folgende Suchbegriffe sind zu kurz und werden ignoriert: ${droppedTokens.join(', ')} (mindestens ${MIN_TOKEN_LENGTH_PG_TRGM} Zeichen pro Wort)`}
          style={{ marginBottom: 8 }}
        />
      )}
      <Table<UpdateDoctorModal_DoctorFragment>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={doctors}
        pagination={{
          current: urlState.page,
          pageSize: urlState.pageSize,
          total: pagedData?.pagedDoctors ? pagedData.pagedDoctors.totalCount : 0,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Zuweisern`,
          showQuickJumper: true,
          showSizeChanger: true,
        }}
        onRow={record => ({ onClick: () => setEditDoctor(record), style: { cursor: 'pointer' } })}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter)}
        loading={loading}
        columns={filteredColumns}
      />
      <CreateDoctorModal
        open={createModalOpen}
        allMeAddresses={allMeAddresses}
        onClose={() => {
          setCreateModalOpen(false);
          refetch();
        }}
        labFragments={metaData?.labs ?? []}
        catalogFragments={metaData?.catalogs ?? []}
        insuranceFragments={metaData?.insurances ?? []}
        specialRateFragments={metaData?.specialRates ?? []}
        themeFragments={metaData?.themes ?? []}
        tagFragments={metaData?.tags ?? []}
      />
      <UpdateDoctorModal
        doctor={editDoctor}
        allMeAddresses={allMeAddresses}
        onClose={() => {
          setEditDoctor(null);
          refetch();
        }}
        labFragments={metaData?.labs ?? []}
        catalogFragments={metaData?.catalogs ?? []}
        insuranceFragments={metaData?.insurances ?? []}
        specialRateFragments={metaData?.specialRates ?? []}
        themeFragments={metaData?.themes ?? []}
        tagFragments={metaData?.tags ?? []}
      />
      <TagManagementModal
        open={tagManagementOpen}
        onClose={() => {
          setTagManagementOpen(false);
          refetchMeta();
        }}
        onChange={() => {
          refetch();
          refetchMeta();
        }}
      />
    </MainContent>
  );
};
